'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo, useState } from 'react';
import { Search, Trash2 } from 'lucide-react';
import { cn } from '~/utils';
import { useI18n } from '~/features/i18n';
import type { ActivityAiChatThread } from './activity-ai-chat-store';
type RecentChatsListVariant = 'sidebar' | 'popover' | 'drawer';

/**
 * Pure helper for the search + limit pipeline. Extracted so the truncation /
 * filter logic is unit-testable without rendering — the component below is
 * mostly presentation glue around this function.
 */
export function selectVisibleThreads(threads: ReadonlyArray<ActivityAiChatThread>, options: {
  limit?: number;
  query?: string;
} = {}): {
  visible: ReadonlyArray<ActivityAiChatThread>;
  truncated: boolean;
  filteredCount: number;
} {
  const normalizedQuery = options.query?.trim().toLowerCase() ?? '';
  const filtered = normalizedQuery.length === 0 ? threads : threads.filter(t => t.title.toLowerCase().includes(normalizedQuery));
  const limit = options.limit;
  const visible = typeof limit === 'number' ? filtered.slice(0, limit) : filtered;
  return {
    visible,
    truncated: typeof limit === 'number' && filtered.length > limit,
    filteredCount: filtered.length
  };
}

/**
 * Shared recent-chats list used by the sidebar landing, the hero popover, and
 * the in-panel history drawer. Renders an optional search input, the list of
 * thread rows, and an optional per-row delete affordance. Surrounding chrome
 * (heading, close button, popover wrapper) stays with each caller — this
 * component owns only the duplicated list logic.
 */
export const RecentChatsList: React.FC<{
  threads: ReadonlyArray<ActivityAiChatThread>;
  activeThreadId?: string;
  onSelect: (threadId: string) => void;
  onDelete?: (threadId: string) => void;
  /** When set, only the first `limit` threads are shown. */
  limit?: number;
  /** Render the searchable filter input above the list. */
  showSearch?: boolean;
  variant: RecentChatsListVariant;
  className?: string;
}> = t0 => {
  const $ = _c(36);
  const {
    threads,
    activeThreadId,
    onSelect,
    onDelete,
    limit,
    showSearch: t1,
    variant,
    className
  } = t0;
  const showSearch = t1 === undefined ? false : t1;
  const i18n = useI18n();
  const [query, setQuery] = useState("");
  const t2 = showSearch ? query : "";
  let t3;
  if ($[0] !== limit || $[1] !== t2 || $[2] !== threads) {
    t3 = selectVisibleThreads(threads, {
      limit,
      query: t2
    });
    $[0] = limit;
    $[1] = t2;
    $[2] = threads;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const {
    visible,
    filteredCount
  } = t3;
  let t4;
  if ($[4] !== i18n) {
    t4 = i18n._("activity.search.ai.historySearchPlaceholder", undefined, {
      message: "Search chats\u2026"
    });
    $[4] = i18n;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const placeholder = t4;
  let t5;
  if ($[6] !== i18n) {
    t5 = i18n._("activity.search.ai.newPrompt", undefined, {
      message: "New search"
    });
    $[6] = i18n;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const untitledLabel = t5;
  const t6 = variant === "drawer" ? "gap-4" : "gap-2";
  let t7;
  if ($[8] !== className || $[9] !== t6) {
    t7 = cn("flex flex-col", t6, className);
    $[8] = className;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== placeholder || $[12] !== query || $[13] !== showSearch || $[14] !== threads || $[15] !== variant) {
    t8 = showSearch && threads.length > 0 ? <div className="relative"><Search className={cn("pointer-events-none absolute top-1/2 -translate-y-1/2", variant === "drawer" ? "left-2 size-5 text-blue-900/45" : "left-2 size-3.5 text-muted-foreground")} strokeWidth={2} aria-hidden={true} /><input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder={placeholder} aria-label={placeholder} className={cn("w-full outline-none", variant === "drawer" ? "rounded-full border border-blue-900/10 bg-stone-50 py-2.5 pl-8 pr-4 text-lg text-blue-900 placeholder:text-blue-900/40 focus:border-secondary/40 focus:bg-white focus-visible:ring-1 focus-visible:ring-secondary/30" : "border-0 bg-transparent py-1.5 pl-7 pr-2 text-xs text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus-visible:ring-0")} /></div> : null;
    $[11] = placeholder;
    $[12] = query;
    $[13] = showSearch;
    $[14] = threads;
    $[15] = variant;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== filteredCount || $[18] !== i18n || $[19] !== showSearch || $[20] !== threads || $[21] !== variant) {
    t9 = showSearch && threads.length > 0 && filteredCount === 0 ? <p className={cn("text-center text-xs", variant === "drawer" ? "rounded-2xl border border-dashed border-blue-900/15 bg-stone-50 px-3 py-4 text-blue-900/60" : "px-2.5 py-3 text-muted-foreground")}>{i18n._("activity.search.ai.historyNoMatches", undefined, {
        message: "No matching chats."
      })}</p> : null;
    $[17] = filteredCount;
    $[18] = i18n;
    $[19] = showSearch;
    $[20] = threads;
    $[21] = variant;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== activeThreadId || $[24] !== i18n || $[25] !== onDelete || $[26] !== onSelect || $[27] !== untitledLabel || $[28] !== variant || $[29] !== visible) {
    t10 = visible.length > 0 ? <ul className={cn(variant === "sidebar" && "flex flex-col gap-1", variant === "drawer" && "flex flex-col gap-2", variant === "popover" && "max-h-64 overflow-y-auto p-1")} role={variant === "popover" ? "listbox" : undefined}>{visible.map(thread => {
        const isActive = thread.id === activeThreadId;
        const title = thread.title && thread.title.length > 0 ? thread.title : untitledLabel;
        return <li key={thread.id} role={variant === "popover" ? "none" : undefined} className={cn(variant === "drawer" && "group/thread relative")}><button type="button" role={variant === "popover" ? "option" : undefined} aria-selected={variant === "popover" ? isActive : undefined} onClick={() => onSelect(thread.id)} className={cn("w-full text-left transition-colors", variant === "sidebar" && "truncate rounded-2xl border border-transparent bg-stone-50 px-3 py-2 text-sm text-blue-900 hover:border-blue-900/10 hover:bg-stone-100", variant === "popover" && cn("flex flex-col gap-0.5 rounded-md px-2.5 py-2 text-sm hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:outline-none", isActive && "bg-accent/60"), variant === "drawer" && cn("flex min-w-0 flex-col items-start gap-1 rounded-3xl border px-8 py-4 text-left", onDelete && "pr-10", isActive ? "border-secondary/40 bg-secondary/5" : "border-transparent bg-stone-50 hover:border-blue-900/10 hover:bg-stone-100"))}>{variant === "sidebar" ? title : <><span className={cn("line-clamp-1", variant === "popover" ? "font-medium text-foreground" : "text-lg font-semibold leading-tight text-blue-900")}>{title}</span><span className={cn("uppercase tracking-wide", variant === "popover" ? "text-2xs text-muted-foreground/80" : "text-sm text-blue-900/40")}>{new Date(thread.createdAt).toLocaleString(i18n.locale)}</span></>}</button>{variant === "drawer" && onDelete ? <button type="button" onClick={() => onDelete(thread.id)} aria-label={i18n._("Delete")} data-testid="recent-chats-delete" className="absolute top-1/2 right-2 inline-flex size-7 -translate-y-1/2 items-center justify-center rounded-full text-blue-900/40 opacity-100 transition-opacity group-hover/thread:opacity-100 hover:bg-red-50 hover:text-red-600 focus:opacity-100 md:opacity-0"><Trash2 className="size-3.5" strokeWidth={2.25} aria-hidden={true} /></button> : null}</li>;
      })}</ul> : null;
    $[23] = activeThreadId;
    $[24] = i18n;
    $[25] = onDelete;
    $[26] = onSelect;
    $[27] = untitledLabel;
    $[28] = variant;
    $[29] = visible;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== t10 || $[32] !== t7 || $[33] !== t8 || $[34] !== t9) {
    t11 = <div className={t7}>{t8}{t9}{t10}</div>;
    $[31] = t10;
    $[32] = t7;
    $[33] = t8;
    $[34] = t9;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  return t11;
};